import { MilestoneMap1 } from '../types/MilestoneMap1';
import { calculateIntervalRange } from './calculateIntervalRange';
import { MilestoneMapInterval } from '../types/MilestoneMap1';
import { ZoneOptions, cloneInZone, toPlainDate } from './zone';

// ---------------------------------------------------------------------------
// Shared interval dropdown types and formatting utilities
// ---------------------------------------------------------------------------

/**
 * Represents a single option in an interval selection dropdown.
 * A null interval means "Latest" (no specific interval filter).
 */
export type IntervalOption = {
  label: string;
  interval: { start: Date; end: Date } | null; // null means "Latest"
};

/**
 * Formats a date interval into a human-readable label for the interval dropdown.
 *
 * Used by DescendantsTab and StatusTab1 to render interval options.
 */
export function formatIntervalLabel(
  interval: { start: Date; end: Date },
  intervalType: MilestoneMapInterval,
  options: ZoneOptions = {},
): string {
  const end = interval.end;
  const timeZone = options.timeZone;
  // Zone-aware view of `end` for getMonth/getFullYear (same instant); plain Date without a zone.
  const endLocal = cloneInZone(end, timeZone);

  switch (intervalType) {
    case 'hours': {
      return end.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      });
    }
    case 'days': {
      return end.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        timeZone,
      });
    }
    case 'weeks': {
      const weekStart = interval.start.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        timeZone,
      });
      const weekEnd = end.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        timeZone,
      });
      return `${weekStart} – ${weekEnd}`;
    }
    case 'months': {
      return end.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone });
    }
    case 'quarters': {
      const month = endLocal.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      return `Q${quarter} ${endLocal.getFullYear()}`;
    }
    case 'years': {
      return endLocal.getFullYear().toString();
    }
    default:
      return end.toLocaleDateString(undefined, { timeZone });
  }
}

/**
 * Formats a date string as a short locale date: "M/D/YY" in the system locale.
 *
 * Extracted from DescendantsTab to avoid duplicating the same locale options
 * wherever a compact date label is needed.
 */
export function formatShortDateString(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

/**
 * Normalizes an interval type to its plural form
 * @param type The interval type to normalize
 * @returns The normalized interval type
 */
function normalizeIntervalType(type: MilestoneMapInterval): MilestoneMapInterval {
  return type.endsWith('s') ? type : (`${type}s` as MilestoneMapInterval);
}

/**
 * Sets the time to the start of the day (00:00:00.000)
 * @param date The date to modify
 * @returns The modified date
 */
export function setStartOfDay(date: Date): Date {
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Sets the time to the end of the day (23:59:59.999)
 * @param date The date to modify
 * @returns The modified date
 */
export function setEndOfDay(date: Date): Date {
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * Gets the start month of a quarter (0-11)
 *
 * Maps any month to the first month of its quarter:
 * Q1 (Jan-Mar): 0 -> 0, 1 -> 0, 2 -> 0
 * Q2 (Apr-Jun): 3 -> 3, 4 -> 3, 5 -> 3
 * Q3 (Jul-Sep): 6 -> 6, 7 -> 6, 8 -> 6
 * Q4 (Oct-Dec): 9 -> 9, 10 -> 9, 11 -> 9
 *
 * @example
 * getQuarterStartMonth(0)  // returns 0  (Jan -> Jan)
 * getQuarterStartMonth(1)  // returns 0  (Feb -> Jan)
 * getQuarterStartMonth(2)  // returns 0  (Mar -> Jan)
 * getQuarterStartMonth(3)  // returns 3  (Apr -> Apr)
 * getQuarterStartMonth(4)  // returns 3  (May -> Apr)
 * getQuarterStartMonth(5)  // returns 3  (Jun -> Apr)
 * getQuarterStartMonth(6)  // returns 6  (Jul -> Jul)
 * getQuarterStartMonth(7)  // returns 6  (Aug -> Jul)
 * getQuarterStartMonth(8)  // returns 6  (Sep -> Jul)
 * getQuarterStartMonth(9)  // returns 9  (Oct -> Oct)
 * getQuarterStartMonth(10) // returns 9  (Nov -> Oct)
 * getQuarterStartMonth(11) // returns 9  (Dec -> Oct)
 *
 * @param month The month to get the quarter start for (0-11)
 * @returns The first month of the quarter (0-11)
 */
export function getQuarterStartMonth(month: number): number {
  return Math.floor(month / 3) * 3;
}

/**
 * Gets the end month of a quarter (0-11)
 *
 * Maps any month to the last month of its quarter:
 * Q1 (Jan-Mar): 0 -> 2, 1 -> 2, 2 -> 2
 * Q2 (Apr-Jun): 3 -> 5, 4 -> 5, 5 -> 5
 * Q3 (Jul-Sep): 6 -> 8, 7 -> 8, 8 -> 8
 * Q4 (Oct-Dec): 9 -> 11, 10 -> 11, 11 -> 11
 *
 * @example
 * getQuarterEndMonth(0)  // returns 2  (Jan -> Mar)
 * getQuarterEndMonth(1)  // returns 2  (Feb -> Mar)
 * getQuarterEndMonth(2)  // returns 2  (Mar -> Mar)
 * getQuarterEndMonth(3)  // returns 5  (Apr -> Jun)
 * getQuarterEndMonth(4)  // returns 5  (May -> Jun)
 * getQuarterEndMonth(5)  // returns 5  (Jun -> Jun)
 * getQuarterEndMonth(6)  // returns 8  (Jul -> Sep)
 * getQuarterEndMonth(7)  // returns 8  (Aug -> Sep)
 * getQuarterEndMonth(8)  // returns 8  (Sep -> Sep)
 * getQuarterEndMonth(9)  // returns 11 (Oct -> Dec)
 * getQuarterEndMonth(10) // returns 11 (Nov -> Dec)
 * getQuarterEndMonth(11) // returns 11 (Dec -> Dec)
 *
 * @param month The month to get the quarter end for (0-11)
 * @returns The last month of the quarter (0-11)
 */
export function getQuarterEndMonth(month: number): number {
  return getQuarterStartMonth(month) + 2;
}

/**
 * Standardizes a date to the start of its quarter
 *
 * This is a high-level function that works with full Date objects.
 * Use this when you need to adjust a date to the start of its quarter,
 * including proper time components (00:00:00.000).
 *
 * @example
 * const date = new Date('2024-04-15 14:30:00')
 * standardizeToQuarterStart(date)
 * // Returns: Date object for 2024-04-01 00:00:00.000
 *
 * const date2 = new Date('2024-07-20 09:15:00')
 * standardizeToQuarterStart(date2)
 * // Returns: Date object for 2024-07-01 00:00:00.000
 *
 * @param date The date to standardize
 * @returns The standardized date at the start of the quarter
 */
export function standardizeToQuarterStart(date: Date, options: ZoneOptions = {}): Date {
  const standardized = cloneInZone(date, options.timeZone);
  const month = standardized.getMonth();
  const quarterStartMonth = getQuarterStartMonth(month);
  // Set the month to the quarter start month
  standardized.setMonth(quarterStartMonth);
  // Set the day to 1 to ensure we're at the start of the month
  standardized.setDate(1);
  setStartOfDay(standardized);

  return toPlainDate(standardized, options.timeZone);
}

/**
 * Standardizes a date to the end of its quarter
 *
 * This is a high-level function that works with full Date objects.
 * Use this when you need to adjust a date to the end of its quarter,
 * including proper time components (23:59:59.999).
 *
 * @example
 * const date = new Date('2024-04-15 14:30:00')
 * standardizeToQuarterEnd(date)
 * // Returns: Date object for 2024-06-30 23:59:59.999
 *
 * const date2 = new Date('2024-07-20 09:15:00')
 * standardizeToQuarterEnd(date2)
 * // Returns: Date object for 2024-09-30 23:59:59.999
 *
 * @param date The date to standardize
 * @returns The standardized date at the end of the quarter
 */
export function standardizeToQuarterEnd(date: Date, options: ZoneOptions = {}): Date {
  const standardized = cloneInZone(date, options.timeZone);
  const month = standardized.getMonth();
  const quarterEndMonth = getQuarterEndMonth(month);

  // Set to the first day of the next month
  standardized.setMonth(quarterEndMonth + 1);
  standardized.setDate(1);
  // Then subtract one day to get the last day of the current month
  standardized.setDate(0);
  setEndOfDay(standardized);

  return toPlainDate(standardized, options.timeZone);
}

/**
 * Standardizes a date to the natural start of an interval type
 * (e.g., start of day, start of month, etc.)
 *
 * @param date The date to standardize
 * @param type The interval type to standardize to
 * @returns The standardized date
 */
export function standardizeStartDate(
  date: Date,
  type: MilestoneMapInterval,
  options: ZoneOptions = {},
): Date {
  const standardized = cloneInZone(date, options.timeZone);

  switch (type) {
    case 'hours':
      // Set to the beginning of the hour
      standardized.setMinutes(0, 0, 0);
      break;
    case 'days':
      // Set to the beginning of the day
      setStartOfDay(standardized);
      break;
    case 'weeks':
      // Set to the beginning of the week (Sunday or Monday depending on locale)
      const day = standardized.getDay();
      const diff = day === 0 ? 0 : day; // Assuming Sunday as start of week (adjust if needed)
      standardized.setDate(standardized.getDate() - diff);
      setStartOfDay(standardized);
      break;
    case 'months':
      // Set to the beginning of the month
      standardized.setDate(1);
      setStartOfDay(standardized);
      break;
    case 'quarters':
      // Use the new quarter standardization function
      return standardizeToQuarterStart(standardized, options);
    case 'years':
      // Set to the beginning of the year
      standardized.setMonth(0, 1);
      setStartOfDay(standardized);
      break;
  }

  return toPlainDate(standardized, options.timeZone);
}

/**
 * Gets the start date of an interval given its end date and type
 * @param endDate The end date of the interval
 * @param intervalType The type of interval
 * @param standardize Whether to standardize the date to the natural start of the interval (default: true)
 * @returns The start date of the interval
 */
export function getIntervalStart(
  endDate: Date,
  intervalType: MilestoneMapInterval,
  standardize: boolean = true,
  options: ZoneOptions = {},
): Date {
  const start = cloneInZone(endDate, options.timeZone);
  const normalizedType = normalizeIntervalType(intervalType);

  switch (normalizedType) {
    case 'hours':
      start.setHours(start.getHours() - 1);
      break;
    case 'days':
      start.setDate(start.getDate() - 1);
      setStartOfDay(start);
      break;
    case 'weeks':
      start.setDate(start.getDate() - 7);
      setStartOfDay(start);
      break;
    case 'months':
      start.setMonth(start.getMonth() - 1);
      setStartOfDay(start);
      break;
    case 'quarters':
      // Use consistent quarter handling
      start.setMonth(start.getMonth() - 3);
      return standardize
        ? standardizeToQuarterStart(start, options)
        : toPlainDate(start, options.timeZone);
    case 'years':
      start.setFullYear(start.getFullYear() - 1);
      setStartOfDay(start);
      break;
  }
  return standardize
    ? standardizeStartDate(start, intervalType, options)
    : toPlainDate(start, options.timeZone);
}

/**
 * Returns the interval end date based on start date and interval type.
 * This is what users expect to see as the "end" of an interval.
 * For example, for October, this returns Oct 31 23:59:59.
 *
 * @param intervalStart The start date of the interval
 * @param type The interval type (hours, days, weeks, months, quarters, years)
 * @param standardize Whether to standardize the date to the natural end of the interval (default: true)
 * @returns The end of the interval as a Date object
 */
export function getIntervalEnd(
  intervalStart: Date,
  type: MilestoneMapInterval,
  standardize: boolean = true,
  options: ZoneOptions = {},
): Date {
  // Begin with the start date, standardizing it first if needed
  const start = standardize
    ? standardizeStartDate(intervalStart, type, options)
    : cloneInZone(intervalStart, options.timeZone);
  const intervalEnd = cloneInZone(start, options.timeZone);
  const normalizedType = normalizeIntervalType(type);

  // Add appropriate time based on interval type
  switch (normalizedType) {
    case 'hours':
      // TODO: Review/Revise. Something about how we're handling this is
      // causing duplicate results right now.
      intervalEnd.setHours(intervalEnd.getHours() + 1);
      break;
    case 'days':
      // For days, we want the end of the current day
      intervalEnd.setDate(intervalEnd.getDate() + 0); // Stay on current day
      setEndOfDay(intervalEnd);
      break;
    case 'weeks':
      // For weeks, we want the end of the current week (6 days after start)
      intervalEnd.setDate(intervalEnd.getDate() + 6);
      setEndOfDay(intervalEnd);
      break;
    case 'months':
      // For months, we want the last day of the current month
      intervalEnd.setMonth(intervalEnd.getMonth() + 1);
      intervalEnd.setDate(0); // JavaScript Date API trick: setting date to 0 gives us the last day of the previous month
      setEndOfDay(intervalEnd);
      break;
    case 'quarters':
      // Use consistent quarter handling
      intervalEnd.setMonth(intervalEnd.getMonth() + 2);
      return standardize
        ? standardizeToQuarterEnd(intervalEnd, options)
        : toPlainDate(intervalEnd, options.timeZone);
    case 'years':
      // For years, we want the last day of the current year
      intervalEnd.setFullYear(intervalEnd.getFullYear() + 1);
      intervalEnd.setMonth(0, 0); // Last day of December
      setEndOfDay(intervalEnd);
      break;
    default:
      throw new Error(`Unsupported interval type: ${type}`);
  }

  // For hours, we don't set end of day
  if (standardize && normalizedType === 'hours') {
    intervalEnd.setMinutes(59, 59, 999);
  }

  return toPlainDate(intervalEnd, options.timeZone);
}

/**
 * Returns the start date of the next interval.
 * This is used internally to calculate the next interval.
 * For example, for October, this returns Nov 1 00:00:00.
 *
 * @param intervalStart The start date of the current interval
 * @param type The interval type (hours, days, weeks, months, quarters, years)
 * @returns The start of the next interval as a Date object
 */
export function getNextIntervalStart(
  intervalStart: Date,
  type: MilestoneMapInterval,
  options: ZoneOptions = {},
): Date {
  const nextStart = cloneInZone(intervalStart, options.timeZone);
  const normalizedType = normalizeIntervalType(type);

  switch (normalizedType) {
    case 'hours':
      nextStart.setHours(nextStart.getHours() + 1);
      break;
    case 'days':
      // For days, we want the start of the next day
      nextStart.setDate(nextStart.getDate() + 1);
      setStartOfDay(nextStart);
      break;
    case 'weeks':
      // For weeks, we want the start of the next week (7 days after start)
      nextStart.setDate(nextStart.getDate() + 7);
      setStartOfDay(nextStart);
      break;
    case 'months':
      // For months, we want the start of the next month
      nextStart.setMonth(nextStart.getMonth() + 1);
      nextStart.setDate(1);
      setStartOfDay(nextStart);
      break;
    case 'quarters':
      // For quarters, we want the start of the next quarter
      nextStart.setMonth(nextStart.getMonth() + 3);
      nextStart.setDate(1);
      setStartOfDay(nextStart);
      break;
    case 'years':
      // For years, we want the start of the next year
      nextStart.setFullYear(nextStart.getFullYear() + 1);
      nextStart.setMonth(0, 1);
      setStartOfDay(nextStart);
      break;
    default:
      throw new Error(`Unsupported interval type: ${type}`);
  }

  return toPlainDate(nextStart, options.timeZone);
}

export function getInterval(
  forIntervalEnding: string,
  milestoneMap: MilestoneMap1,
  intervalOverride?: MilestoneMapInterval,
  options: ZoneOptions = {},
) {
  const [intervalStart, intervalEnd] = calculateIntervalRange(
    forIntervalEnding,
    milestoneMap,
    intervalOverride,
    options,
  );
  return intervalStart && intervalEnd ? { start: intervalStart, end: intervalEnd } : undefined;
}

/**
 * Returns the maximum number of intervals (columns) among all maps.
 * Used to determine the total width of the master grid in a grid
 * where all columns are the same width (e.g. stacked view).
 *
 * @param milestoneMaps - Array of milestoneMap objects with intervals
 * @returns The maximum number of intervals among all maps
 */
export function getMaxIntervals<T extends { intervals: Array<{ start: Date; end: Date }> }>(
  milestoneMaps: T[],
): number {
  return Math.max(0, ...milestoneMaps.map((m) => m.intervals.length));
}

/**
 * Returns the smallest interval type among all maps.
 * Throws an error if any map uses 'hours' as it's not supported.
 *
 * Interval size order (smallest to largest):
 * - days
 * - weeks
 * - months
 * - quarters
 * - years
 */
// TODO:
// - add validation for interval types
export function getSmallestIntervalType(
  maps: Array<{ milestoneMap: { interval: MilestoneMapInterval } }>,
): MilestoneMapInterval {
  if (maps.length === 0) {
    throw new Error('No maps provided to determine smallest interval type');
  }

  // Check for unsupported interval type
  const hasHours = maps.some((m) => m.milestoneMap.interval === 'hours');
  if (hasHours) {
    throw new Error('Hours interval type is not supported');
  }

  // Define interval size order (smallest to largest)
  const intervalOrder: MilestoneMapInterval[] = ['days', 'weeks', 'months', 'quarters', 'years'];

  // Get all unique interval types from maps
  const intervalTypes = Array.from(new Set(maps.map((m) => m.milestoneMap.interval)));

  // Find the smallest interval type
  const smallestInterval = intervalOrder.find((interval) => intervalTypes.includes(interval));

  if (!smallestInterval) {
    throw new Error('Could not determine smallest interval type');
  }

  return smallestInterval;
}

/**
 * Returns a standardized array of intervals of the requested type spanning from the earliest
 * start date to the latest end date in the input intervals.
 *
 * @param intervals Array<{ start: Date; end: Date }> - An array of intervals with varying types (hours, days, weeks, months, quarters, years)
 * @param intervalType - The desired uniform interval type for the output
 * @returns A sorted array of intervals of the requested type spanning the full input range
 */
// TODO:
// - add validation for interval types
export function getUniqueIntervals(
  intervals: Array<{ start: Date; end: Date }>,
  intervalType: MilestoneMapInterval,
  options: ZoneOptions = {},
): Array<{ start: Date; end: Date }> {
  // If no intervals provided, return empty array
  if (!intervals.length) return [];

  // Step 1: Find the earliest start and latest end date across all input intervals
  const earliestStart = new Date(
    Math.min(...intervals.map((interval) => interval.start.getTime())),
  );
  const latestEnd = new Date(Math.max(...intervals.map((interval) => interval.end.getTime())));

  // Step 2: Generate uniform intervals of the requested type from earliest start to latest end
  const result: Array<{ start: Date; end: Date }> = [];

  // Standardize the start and end dates to ensure clean intervals
  const standardizedStart = standardizeStartDate(earliestStart, intervalType, options);
  let currentStart = standardizedStart;

  // Safety check: If standardized start is after latest end, return empty array
  if (currentStart >= latestEnd) {
    console.error('getUniqueIntervals: standardized start is after latest end:', {
      earliestStart,
      latestEnd,
      standardizedStart,
    });
    return [];
  }

  // Generate intervals until we reach or exceed the latest end date
  let iterations = 0;
  const MAX_ITERATIONS = 10000; // Safety limit to prevent infinite loops

  while (currentStart < latestEnd && iterations < MAX_ITERATIONS) {
    const intervalEnd = getIntervalEnd(currentStart, intervalType, true, options);

    // Safety check: If interval end is not greater than current start, break to prevent infinite loop
    if (intervalEnd <= currentStart) {
      console.error('Interval end is not greater than current start:', {
        currentStart,
        intervalEnd,
        intervalType,
      });
      break;
    }

    result.push({
      start: new Date(currentStart),
      end: new Date(intervalEnd),
    });

    // Move to the next interval
    currentStart = getNextIntervalStart(currentStart, intervalType, options);
    iterations++;
  }

  // Log warning if we hit the iteration limit
  if (iterations >= MAX_ITERATIONS) {
    console.warn('getUniqueIntervals hit maximum iteration limit:', {
      intervalType,
      earliestStart,
      latestEnd,
      resultLength: result.length,
    });
  }

  // Step 3: Ensure we include the latest end date if it doesn't align perfectly
  if (result.length && result[result.length - 1].end < latestEnd) {
    const lastInterval = getIntervalEnd(result[result.length - 1].end, intervalType, true, options);
    // Safety check: Only add if lastInterval is greater than the previous end
    if (lastInterval > result[result.length - 1].end) {
      result.push({
        start: new Date(result[result.length - 1].end),
        end: new Date(lastInterval),
      });
    }
  }

  return result;
}
